import { Component, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Checkbox, CheckboxChangeEvent } from 'primeng/checkbox';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { RadioButton } from 'primeng/radiobutton';
import { Select } from 'primeng/select';

import { UsersService } from '../../../../core/services/users.service';
import { MayorapDatepickerComponent } from '../../../../shared/components/mayorap-datepicker/mayorap-datepicker.component';
import { AgregarPersonaDatosBasicos, AgregarPersonaSexo } from '../../agregar-persona-form.interface';
import {
  CELULAR_PREFIJO_DEFAULT,
  CELULAR_PREFIJOS,
} from '../../../../shared/helpers/celular-prefijos.data';
import {
  capitalizeWords,
  sanitizeApellidoPersona,
  sanitizeNombrePersona,
} from '../../../../shared/helpers/nombre-texto.helper';
import { DuplicatePersonResult } from '../../../../shared/interfaces/user.interface';

@Component({
  selector: 'app-agregar-persona-paso-datos-basicos',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    Button,
    InputText,
    Dialog,
    Select,
    MayorapDatepickerComponent,
    Checkbox,
    RadioButton,
    ConfirmDialog,
  ],
  templateUrl: './paso-datos-basicos.component.html',
  styleUrl: './paso-datos-basicos.component.scss',
})
export class AgregarPersonaPasoDatosBasicosComponent {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly usersService = inject(UsersService);
  private ultimaConsulta = '';
  private consultaId = 0;

  /** Prefill en modo editar. */
  readonly initial = input<AgregarPersonaDatosBasicos | null>(null);

  readonly next = output<AgregarPersonaDatosBasicos>();

  readonly prefijos = CELULAR_PREFIJOS;
  readonly sexoOptions: { label: string; value: AgregarPersonaSexo }[] = [
    { label: 'Hombre', value: 'hombre' },
    { label: 'Mujer', value: 'mujer' },
  ];

  readonly segundoNombreEnabled = signal(false);
  readonly duplicadoVisible = signal(false);
  readonly duplicadoTitulo = signal('');
  readonly duplicadoMensaje = signal('');

  readonly form = this.fb.nonNullable.group({
    nombre: ['', Validators.required],
    apellido: ['', Validators.required],
    nombre2: [''],
    prefijo: [CELULAR_PREFIJO_DEFAULT],
    celular: [''],
    nacimiento: this.fb.control<Date | null>(null, Validators.required),
    sexo: this.fb.nonNullable.control<AgregarPersonaSexo>('hombre', Validators.required),
  });

  /** Minusculas sin tildes. Nombre sin espacios; apellido con uno solo. */
  private readonly _sanitizeNombres = (() => {
    const fields = ['nombre', 'apellido', 'nombre2'] as const;
    for (const field of fields) {
      this.form.controls[field].valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          const next =
            field === 'apellido'
              ? sanitizeApellidoPersona(value ?? '')
              : sanitizeNombrePersona(value ?? '');
          if (next !== value) {
            this.form.controls[field].setValue(next, { emitEvent: false });
          }
        });
    }
    return true;
  })();


  private readonly _applyInitial = effect(() => {
    const data = this.initial();
    if (!data) {
      return;
    }
    this.form.patchValue({
      nombre: data.nombre,
      apellido: data.apellido,
      nombre2: data.nombre2,
      prefijo: data.prefijo,
      celular: data.celular,
      nacimiento: data.nacimiento,
      sexo: data.sexo,
    });
    if (data.nombre2?.trim()) {
      this.segundoNombreEnabled.set(true);
    }
  });

  showFieldError(field: 'nombre' | 'apellido' | 'nacimiento'): boolean {
    const control = this.form.controls[field];
    return control.invalid && control.touched;
  }

  onSegundoNombreChange(event: CheckboxChangeEvent): void {
    if (!event.checked) {
      return;
    }

    this.confirmationService.confirm({
      header: 'Habilitar segundo nombre',
      message: '¿Estás seguro de habilitar el segundo nombre? Se usa en casos especiales.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Habilitar',
      rejectLabel: 'Cancelar',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        this.segundoNombreEnabled.set(true);
      },
    });
  }

  onNombreBlur(): void {
    this.verificarDuplicado();
  }

  onApellidoBlur(): void {
    const apellido = this.form.controls.apellido.value.trim();
    if (apellido !== this.form.controls.apellido.value) {
      this.form.controls.apellido.setValue(apellido);
    }
    this.verificarDuplicado();
  }

  cerrarDuplicado(): void {
    this.duplicadoVisible.set(false);
  }

  onSubmit(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const raw = this.form.getRawValue();
    if (!raw.nacimiento) {
      return;
    }

    const datos: AgregarPersonaDatosBasicos = {
      nombre: sanitizeNombrePersona(raw.nombre),
      apellido: sanitizeApellidoPersona(raw.apellido).trim(),
      nombre2: sanitizeNombrePersona(raw.nombre2),
      prefijo: raw.prefijo,
      celular: raw.celular,
      nacimiento: raw.nacimiento,
      sexo: raw.sexo,
    };

    this.next.emit(datos);
  }

  private verificarDuplicado(): void {
    const nombre = sanitizeNombrePersona(this.form.controls.nombre.value);
    const apellido = sanitizeApellidoPersona(this.form.controls.apellido.value).trim();
    if (!nombre || !apellido || this.esLaPersonaEnEdicion(nombre, apellido)) {
      return;
    }

    const clave = `${nombre}|${apellido}`;
    if (clave === this.ultimaConsulta) {
      return;
    }
    this.ultimaConsulta = clave;
    const consultaId = ++this.consultaId;

    this.usersService
      .buscarDuplicado(nombre, apellido)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (consultaId !== this.consultaId || !response.data.exists) {
            return;
          }
          this.mostrarDuplicado(response.data);
        },
        error: () => {
          if (consultaId !== this.consultaId) {
            return;
          }
          this.ultimaConsulta = '';
          this.duplicadoTitulo.set('Error');
          this.duplicadoMensaje.set('No se pudo verificar si la persona existe.');
          this.duplicadoVisible.set(true);
        },
      });
  }

  private esLaPersonaEnEdicion(nombre: string, apellido: string): boolean {
    const data = this.initial();
    if (!data) {
      return false;
    }
    return (
      sanitizeNombrePersona(data.nombre) === nombre &&
      sanitizeApellidoPersona(data.apellido).trim() === apellido
    );
  }

  private mostrarDuplicado(data: DuplicatePersonResult): void {
    const nombre = capitalizeWords(data.personName);
    const lider = capitalizeWords(data.lider);
    const tieneLider = lider !== '' && lider.toLowerCase() !== 'sin lider' && lider.toLowerCase() !== 'evadir';

    if (data.isInTrash) {
      const liderTexto = tieneLider
        ? ` Su líder era ${lider}.`
        : ' No tenía líder asignado.';
      this.duplicadoTitulo.set('Persona en papelera de datos');
      this.duplicadoMensaje.set(
        `${nombre} está en la papelera de datos.${liderTexto} Un administrador puede restaurar esta persona desde la papelera.`,
      );
    } else {
      const liderTexto = tieneLider ? ` Su líder es ${lider}.` : ' No tiene líder asignado.';
      this.duplicadoTitulo.set('Persona ya registrada');
      this.duplicadoMensaje.set(
        `${nombre} ya está registrada en Mayorap.${liderTexto} Si necesitás registrar a esta persona, hablá con un administrador.`,
      );
    }

    this.limpiarPorDuplicado();
    this.duplicadoVisible.set(true);
  }

  private limpiarPorDuplicado(): void {
    this.ultimaConsulta = '';
    this.consultaId += 1;
    this.segundoNombreEnabled.set(false);
    this.form.reset({
      nombre: '',
      apellido: '',
      nombre2: '',
      prefijo: CELULAR_PREFIJO_DEFAULT,
      celular: '',
      nacimiento: null,
      sexo: 'hombre',
    });
  }
}